import { useMutation, useQueryClient } from "@tanstack/react-query";

import { addFieldCropPractice, addFieldCropPracticeAdmin } from "services";

import { getEnvironment, invalidateCarbonProgramQueriesByChunk, invalidateQueriesByChunk } from "utils";

import {
  ADD_FIELD_CROP_PRACTICE,
  GET_ADMIN_FARM_ORGANIC_FERTILIZER_PRODUCTION,
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FARM_ORGANIC_FERTILIZER_PRODUCTION,
  GET_FARM_RESILIENCE_SCORE,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_PRACTICES,
  GET_FIELD_PRACTICE_FILTER_OPTIONS,
  GET_FIELD_PRACTICE_TIMESTAMPS,
  GET_INCENTIVES_POTENTIAL_PAYOUTS,
  GET_VERIFICATION_EDITING_REQUESTS,
} from "consts";

import { CropPracticesRequestBody } from "models";

type MutateParams = {
  fieldId: string;
  requestBody: CropPracticesRequestBody;
};

export const useAddFieldCropPractice = ({ fieldId, farmId }: { fieldId: string; farmId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ADD_FIELD_CROP_PRACTICE, fieldId],
    mutationFn: ({ requestBody }: { requestBody: MutateParams["requestBody"] }) => mutate({ fieldId, requestBody }),
    onSuccess: () => {
      invalidateCarbonProgramQueriesByChunk(fieldId);
      queryClient.invalidateQueries({ queryKey: [GET_INCENTIVES_POTENTIAL_PAYOUTS] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_FILTER_OPTIONS] });
      queryClient.invalidateQueries({ queryKey: [GET_FARM_RESILIENCE_SCORE] });

      invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
      queryClient.refetchQueries({ queryKey: [GET_FIELD_PRACTICES, fieldId] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_TIMESTAMPS, fieldId] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });
      queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
      if (farmId) {
        queryClient.invalidateQueries({ queryKey: [GET_FARM_ORGANIC_FERTILIZER_PRODUCTION, farmId] });
        queryClient.invalidateQueries({ queryKey: [GET_ADMIN_FARM_ORGANIC_FERTILIZER_PRODUCTION, farmId] });
      }
    },
  });
};

const mutate = ({ fieldId, requestBody }: MutateParams) => {
  const isAdmin = getEnvironment().admin;

  if (isAdmin) {
    return addFieldCropPracticeAdmin({ fieldId, requestBody });
  }

  return addFieldCropPractice({ fieldId, requestBody });
};
